/** Defaults tab §B — the operator's trip settings (backend plan 2026-10-07 §B): the
 *  config-stored values tours get from their operator, grouped by how tours use them.
 *
 *  Each row shows three things on one geometry in both modes (Zero-Re-Layout): the
 *  setting, what is stored here (view: words; edit: a Not set / value segmented control in
 *  the same box), and what tours get with its source from the server (`effective` +
 *  `source` — never the schema's `default`). Under them a reserved caption line holds the
 *  config path, the "was …" + revert of a changed value (amber, edit mode only) and the
 *  row's note.
 *
 *  Edit (with `console_top_manage`) seeds a react-hook-form draft from the GET; Save opens
 *  the configuration save dialog over the operator's config with exactly the changed paths
 *  applied (the legacy `defaults.chat` removal included, so the diff shows it), and its
 *  confirm PATCHes `set` / `unset` with the comment and the GET's change timestamp. A 409
 *  stays in the dialog. Settings that live elsewhere are shown read-only with where they
 *  are changed. */
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, IconButton, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type Control } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  defaultTagCategoriesKey,
  patchTripDefaults,
  tripDefaultsKey,
  type OperatorTripDefaults,
  type OperatorTripDefaultSetting,
  type OperatorTripDefaultsPatch,
} from '@/api/tourOperatorDefaults';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { FieldHelp, type FieldHelpEntry } from '@/components/FieldHelp';
import { Segmented } from '@/components/Segmented';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';
import { usePermissions } from '@/hooks/usePermissions';

import { SaveConfigChangesDialog } from '../config/SaveConfigChangesDialog';
import { easyOnboardHref, useEasyGoLiveGate } from '../onboard/useEasyGoLiveGate';
import { launchPolicyActions, operatorPreCheckInHref } from '../tenantLinks';
import { DefaultsSection } from './DefaultsSection';
import { DEFAULTS_HELP } from './fieldHelp';
import { apiErrorText, useTripDefaults } from './queries';
import {
  EDITABLE_SETTINGS,
  LEGACY_GROUP_CHAT,
  LEVEL_WORDS,
  READ_ONLY_LABELS,
  SETTING_GROUPS,
  applyOps,
  changedSettings,
  fieldOf,
  formValuesOf,
  patchOps,
  sourceWords,
  tokenOf,
  valueWords,
  type FormValues,
  type PatchOps,
  type ReadOnlyKey,
  type SettingDef,
  type Token,
} from './tripSettings';

const BOOL = z.enum(['unset', 'on', 'off']);
const LEVEL = z.enum(['unset', '0', '1', '2']);
/** Exactly the tokens the controls offer — no stricter than what they can send. */
const schema = z.object(
  Object.fromEntries(
    EDITABLE_SETTINGS.map((d) => [fieldOf(d.key), d.kind === 'level' ? LEVEL : BOOL]),
  ),
);

const BOOL_OPTIONS = [
  { value: 'unset', label: 'Not set' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
] as const;
const LEVEL_OPTIONS = [
  { value: 'unset', label: 'Not set' },
  ...LEVEL_WORDS.map((w, i) => ({ value: String(i) as Token, label: w })),
] as const;

const tokenWords = (def: SettingDef, t: Token | undefined): string =>
  t === undefined || t === 'unset'
    ? 'Not set'
    : def.kind === 'level'
      ? (LEVEL_WORDS[Number(t)] ?? t)
      : t === 'on'
        ? 'On'
        : 'Off';

// One geometry for every row (and the header): columns wrap together on a narrow page.
const LABEL_COL = { flex: '1 1 240px', minWidth: 0 } as const;
const CONTROL_COL = { flex: '0 0 340px', maxWidth: '100%', minWidth: 0 } as const;
const EFFECT_COL = { flex: '1 1 220px', minWidth: 0 } as const;
const ROW_SX = { display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25 } as const;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
/** The segmented control's height (28px segments, 2px padding, 1px frame): every cell of
 *  a row reserves it in view mode too, so Edit moves nothing. */
const CONTROL_PX = 34;

interface Props {
  to: TourOperatorDetail;
  canManage: boolean;
  editing: boolean;
  editBlockedReason: string | null;
  onEditingChange: (editing: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function TripSettingsSection({
  to,
  canManage,
  editing,
  editBlockedReason,
  onEditingChange,
  onDirtyChange,
}: Props) {
  const query = useTripDefaults(to.id);
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  // The default launch policy is switched elsewhere, policy-only; an easy tenant still under
  // the outbound guard goes live from its easy setup instead (useEasyGoLiveGate). Asked by
  // whoever may (the easy state needs both permissions); others see the row as before.
  const askedEasy = canManage && hasPerm('console_preflight_manage');
  const easy = useEasyGoLiveGate(to.id, askedEasy);

  // The draft's start: the tokens it diffs against and the timestamp its PATCH carries.
  const [draft, setDraft] = useState<{ initial: FormValues; loadedTs: string | null } | null>(null);
  const isEditing = editing && draft !== null;
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: {} });
  const values = useWatch({ control: form.control }) as FormValues;
  const changed = useMemo(
    () => (isEditing ? changedSettings(draft.initial, values) : []),
    [isEditing, draft, values],
  );
  const count = changed.length;
  const dirty = count > 0;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // ---- save -----------------------------------------------------------------------
  // The open save dialog's ops. Never re-keyed: a remount would drop the typed comment.
  const [pending, setPending] = useState<{
    ops: PatchOps;
    config: Record<string, unknown>;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const startEdit = (data: OperatorTripDefaults) => {
    const initial = formValuesOf(data.settings);
    form.reset(initial);
    setDraft({ initial, loadedTs: data.config_last_modified_ts });
    setSaveError(null);
    onEditingChange(true);
  };
  const leaveEdit = () => {
    setDraft(null);
    setPending(null);
    setSaveError(null);
    onEditingChange(false);
    // Re-read, so the next edit starts from the server: after a 409, or when the config
    // dialog found the configuration changed and blocked the save, the cached settings and
    // operator are stale — editing them again would hit the same wall.
    void qc.invalidateQueries({ queryKey: tripDefaultsKey(to.id) });
    void qc.invalidateQueries({ queryKey: ['tourOperators', 'detail', to.id] });
  };

  const mutation = useMutation({
    mutationFn: (body: OperatorTripDefaultsPatch) => patchTripDefaults(to.id, body),
    onSuccess: (data) => {
      qc.setQueryData(tripDefaultsKey(to.id), data);
      // §A's header reads use_operator_defaults / allow_tour_tag_categories_modification.
      void qc.invalidateQueries({ queryKey: defaultTagCategoriesKey(to.id) });
      snackbar.show('Trip settings saved.', 'success');
      leaveEdit();
    },
    onError: (e) => {
      // Only a stale config has a remedy here — start again from the saved settings. Another
      // 409 (`invalid_path`: the stored structure conflicts) is the server's words alone.
      const stale =
        e instanceof ConsoleApiError && e.status === 409 && e.body?.code === 'config_modified';
      setSaveError(
        stale
          ? `${apiErrorText(e)} Cancel, then Discard and edit again to start from the saved settings.`
          : apiErrorText(e),
      );
    },
  });

  const openSave = () =>
    void form.handleSubmit(
      (v) => {
        if (!draft) return;
        const ops = patchOps(draft.initial, v);
        setSaveError(null);
        setPending({ ops, config: applyOps(to.config, ops) });
      },
      () =>
        snackbar.show(
          'Nothing was saved — a setting has a value the server would refuse.',
          'error',
        ),
    )();

  const settings = query.data?.settings;
  const byKey = useMemo(
    () => new Map((settings ?? []).map((s) => [s.key, s] as const)),
    [settings],
  );
  const legacyChat = query.data?.legacy[LEGACY_GROUP_CHAT];

  return (
    <DefaultsSection
      id="trip-settings"
      topId={to.id}
      title="Trip settings"
      description="Settings tours get from this operator, stored in its configuration. Not set removes the key, so tours keep their own value — or the parent operator’s, or the built-in one, applies."
      edit={{
        canManage,
        editing: isEditing,
        editBlockedReason:
          editBlockedReason ?? (query.data ? null : 'The trip settings have not loaded'),
        count,
        saving: mutation.isPending,
        dialogOpen: pending !== null,
        onEdit: () => query.data && startEdit(query.data),
        onDiscard: leaveEdit,
        onSave: openSave,
      }}
    >
      {query.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn’t load the trip settings. {apiErrorText(query.error, '')}
        </Alert>
      ) : !query.data ? (
        <Box sx={{ display: 'grid', gap: 1 }} data-defaults-loading>
          <Skeleton variant="rounded" height={320} />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3 }} data-trip-settings>
          <Box
            aria-hidden
            sx={{
              ...ROW_SX,
              typography: 'caption',
              fontWeight: 600,
              color: 'text.secondary',
              mb: -2,
            }}
          >
            <Box sx={LABEL_COL}>Setting</Box>
            <Box sx={CONTROL_COL}>Stored here</Box>
            <Box sx={EFFECT_COL}>Tours get</Box>
          </Box>
          {SETTING_GROUPS.map((g) => (
            <Group key={g.id} id={g.id} title={g.title} sentence={g.sentence}>
              {g.settings.map((def) => {
                const s = byKey.get(def.key);
                return (
                  <EditableRow
                    key={def.key}
                    def={def}
                    setting={s}
                    editing={isEditing}
                    control={form.control}
                    initial={draft?.initial[fieldOf(def.key)]}
                    onRevert={(t) => form.setValue(fieldOf(def.key), t, { shouldDirty: true })}
                    parentName={to.parent_name}
                    note={
                      def.key === 'defaults.has_group_chat' && legacyChat != null
                        ? `Also stored under the legacy key ${LEGACY_GROUP_CHAT} (${valueWords('bool', legacyChat)}) — saving this setting removes it.`
                        : def.key === 'defaults.mytrips_display'
                          ? 'Stored for the dashboard; the guest app doesn’t apply it yet.'
                          : null
                    }
                  />
                );
              })}
            </Group>
          ))}
          <Group
            id="elsewhere"
            title="Set elsewhere"
            sentence="Shown for reference — each is changed where it lives."
          >
            {(['selfreg_verify_email', 'selfreg_verify_phone'] as const).map((key) => (
              <ReadOnlyRow
                key={key}
                topId={to.id}
                label={READ_ONLY_LABELS[key]}
                setting={byKey.get(key)}
                parentName={to.parent_name}
                readOnlyKey={key}
              />
            ))}
            <FactRow
              label="Default launch policy"
              help={DEFAULTS_HELP.default_launch_policy}
              stored={
                to.default_launch_policy_id != null ? (
                  <Typography variant="body2" component="span">
                    <ValueActions
                      actions={launchPolicyActions(
                        { id: to.default_launch_policy_id, label: to.default_launch_policy_label },
                        { id: to.id, code: to.code },
                      )}
                    >
                      {to.default_launch_policy_label ?? `#${to.default_launch_policy_id}`}
                    </ValueActions>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None
                  </Typography>
                )
              }
              effect={
                !askedEasy || easy.open ? (
                  'Tours without a launch policy of their own use it. Set on the operator record.'
                ) : (
                  <>
                    Tours without a launch policy of their own use it.{' '}
                    {easy.blocked && (
                      <>
                        This tenant sets it when it goes live from its easy setup; until then
                        automatic guest messages stay blocked.{' '}
                        <Link component={RouterLink} to={easyOnboardHref(to.id)}>
                          Go live in easy setup
                        </Link>
                      </>
                    )}
                    {easy.unknown && (
                      <>
                        Could not check whether this tenant goes live through easy setup.{' '}
                        <Link component="button" variant="body2" onClick={easy.retry}>
                          Try again
                        </Link>
                      </>
                    )}
                  </>
                )
              }
              dataKey="default_launch_policy"
            />
            <FactRow
              label="Pre-check-in settings"
              stored={
                <Link component={RouterLink} to={operatorPreCheckInHref(to.id)} variant="body2">
                  Change them on the Pre-check-in tab
                </Link>
              }
              effect="A separate record; the dashboard edits it too."
              dataKey="pre_check_in"
            />
          </Group>
        </Box>
      )}

      {pending && (
        <SaveConfigChangesDialog
          open
          tourOperatorId={to.id}
          originalConfig={to.config ?? {}}
          pendingConfig={pending.config}
          loadedConfigTs={draft?.loadedTs ?? null}
          saving={mutation.isPending}
          saveError={saveError}
          onConfirm={({ comment, ifUnmodifiedSince }) =>
            mutation.mutate({
              set: pending.ops.set,
              unset: pending.ops.unset,
              comment,
              if_unmodified_since: ifUnmodifiedSince,
            })
          }
          onCancel={() => {
            setPending(null);
            setSaveError(null);
          }}
        />
      )}
    </DefaultsSection>
  );
}

function Group({
  id,
  title,
  sentence,
  children,
}: {
  id: string;
  title: string;
  sentence: string;
  children: ReactNode;
}) {
  const titleId = useId();
  return (
    <Box component="section" aria-labelledby={titleId} data-settings-group={id}>
      <Typography id={titleId} component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, maxWidth: '80ch' }}>
        {sentence}
      </Typography>
      {children}
    </Box>
  );
}

/** A setting's label with its help, and its config path in the caption line. */
function LabelCell({ id, label, help }: { id: string; label: string; help?: FieldHelpEntry }) {
  return (
    <Box
      sx={{ ...LABEL_COL, display: 'flex', alignItems: 'center', gap: 0.5, minHeight: CONTROL_PX }}
    >
      <Typography id={id} variant="body2" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      {help && <FieldHelp help={help} />}
    </Box>
  );
}

/** What tours get, and from where. */
function EffectCell({
  setting,
  kind,
  parentName,
}: {
  setting: OperatorTripDefaultSetting | undefined;
  kind: SettingDef['kind'];
  parentName: string | null;
}) {
  if (!setting) return <Box sx={EFFECT_COL} />;
  const source = sourceWords(setting.source, parentName, setting.source_path);
  return (
    <Box sx={{ ...EFFECT_COL, display: 'flex', alignItems: 'center', minHeight: CONTROL_PX }}>
      <Typography variant="body2" data-effective>
        {setting.source === 'unset' ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {source}
          </Box>
        ) : (
          <>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {valueWords(kind, setting.effective)}
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' · '}
              {source}
            </Box>
          </>
        )}
      </Typography>
    </Box>
  );
}

const CAPTION_SX = {
  ...ROW_SX,
  minHeight: 20,
  typography: 'caption',
  color: 'text.secondary',
} as const;

function EditableRow({
  def,
  setting,
  editing,
  control,
  initial,
  onRevert,
  parentName,
  note,
}: {
  def: SettingDef;
  setting: OperatorTripDefaultSetting | undefined;
  editing: boolean;
  control: Control<FormValues>;
  initial: Token | undefined;
  onRevert: (t: Token) => void;
  parentName: string | null;
  note: string | null;
}) {
  const labelId = useId();
  const name = fieldOf(def.key);
  const value = useWatch({ control, name }) as Token | undefined;
  const changed = editing && initial !== undefined && value !== initial;
  const storedToken = setting ? tokenOf(def, setting.own) : undefined;
  const options = def.kind === 'level' ? LEVEL_OPTIONS : BOOL_OPTIONS;

  return (
    <Box
      data-setting={def.key}
      data-state={changed ? 'changed' : undefined}
      sx={{ borderTop: 1, borderColor: 'divider', pt: 0.75, pb: 0.25 }}
    >
      <Box sx={{ ...ROW_SX, alignItems: 'center' }}>
        <LabelCell id={labelId} label={def.label} help={def.help} />
        <Box sx={{ ...CONTROL_COL, minHeight: CONTROL_PX, display: 'flex', alignItems: 'center' }}>
          {editing ? (
            <Controller
              control={control}
              name={name}
              render={({ field }) => (
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    bgcolor: changed ? CHANGED_TINT : 'transparent',
                    transition: 'background-color 150ms',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  <Segmented<Token>
                    value={(field.value as Token | undefined) ?? 'unset'}
                    onChange={field.onChange}
                    options={options}
                    labelledBy={labelId}
                    dataAttr="setting-control"
                  />
                </Box>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              color={storedToken === 'unset' ? 'text.secondary' : 'text.primary'}
              data-stored
            >
              {tokenWords(def, storedToken)}
            </Typography>
          )}
        </Box>
        <EffectCell setting={setting} kind={def.kind} parentName={parentName} />
      </Box>
      <Box sx={CAPTION_SX}>
        <Box sx={{ ...LABEL_COL, fontFamily: MONO, fontSize: 11.5 }}>{def.key}</Box>
        {/* One line high in both modes: the revert button must not grow the row. */}
        <Box sx={{ ...CONTROL_COL, display: 'flex', alignItems: 'center', gap: 0.5, height: 20 }}>
          {changed && initial !== undefined && (
            <>
              <span data-caption="changed">was {tokenWords(def, initial)}</span>
              <Tooltip title={`Revert to ${tokenWords(def, initial)}`}>
                <IconButton
                  size="small"
                  aria-label={`Revert ${def.label}`}
                  onClick={() => onRevert(initial)}
                  sx={{ p: 0.25, my: -0.5 }}
                >
                  <ReplayIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <Box sx={EFFECT_COL} data-note={note ? '' : undefined}>
          {note}
        </Box>
      </Box>
    </Box>
  );
}

function ReadOnlyRow({
  topId,
  label,
  setting,
  parentName,
  readOnlyKey,
}: {
  topId: number;
  label: string;
  setting: OperatorTripDefaultSetting | undefined;
  parentName: string | null;
  readOnlyKey: ReadOnlyKey;
}) {
  const labelId = useId();
  return (
    <Box
      data-setting={readOnlyKey}
      sx={{ borderTop: 1, borderColor: 'divider', pt: 0.75, pb: 0.25 }}
    >
      <Box sx={{ ...ROW_SX, alignItems: 'center' }}>
        <LabelCell id={labelId} label={label} help={DEFAULTS_HELP.selfreg_verify} />
        <Box
          sx={{
            ...CONTROL_COL,
            minHeight: CONTROL_PX,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant="body2"
            color={setting?.own == null ? 'text.secondary' : 'text.primary'}
            data-stored
          >
            {setting?.own == null ? 'Not set' : valueWords('bool', setting.own)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
          >
            <LockOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
            read-only here
          </Typography>
        </Box>
        <EffectCell setting={setting} kind="bool" parentName={parentName} />
      </Box>
      <Box sx={CAPTION_SX}>
        <Box sx={{ ...LABEL_COL, fontFamily: MONO, fontSize: 11.5 }}>
          {setting?.source_path ?? setting?.path}
        </Box>
        <Box sx={CONTROL_COL}>
          <Link component={RouterLink} to={`/tour-operators/${topId}/configuration`}>
            Change it in Configuration
          </Link>
        </Box>
        <Box sx={EFFECT_COL} />
      </Box>
    </Box>
  );
}

function FactRow({
  label,
  help,
  stored,
  effect,
  dataKey,
}: {
  label: string;
  help?: FieldHelpEntry;
  stored: ReactNode;
  effect: ReactNode;
  dataKey: string;
}) {
  const labelId = useId();
  return (
    <Box data-setting={dataKey} sx={{ borderTop: 1, borderColor: 'divider', py: 0.75 }}>
      <Box sx={{ ...ROW_SX, alignItems: 'center' }}>
        <LabelCell id={labelId} label={label} help={help} />
        <Box sx={{ ...CONTROL_COL, minHeight: CONTROL_PX, display: 'flex', alignItems: 'center' }}>
          {stored}
        </Box>
        <Box sx={{ ...EFFECT_COL, display: 'flex', alignItems: 'center', minHeight: CONTROL_PX }}>
          <Typography variant="body2" color="text.secondary">
            {effect}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
